
//
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { usePortfolioStore } from './stores/portfolioStore';
import { fetchNativeBalance, fetchTokenBalances } from './api/onchain';
import { getWalletAddresses, deleteWalletAddress, updateWalletAddress, createConnectionEntity, createWalletAddress } from './api/connections';
import { OnchainService } from './features/onchain/onchainService';
import { getNativeAssetIdForChain, getWalletChain, normalizeStoreOnchainAssets } from './features/onchain/storeOnchainAsset';
import { createSyncTimestamp, formatSyncDate } from './utils/syncDate';

const TOKEN_LISTS = {
  ethereum: [
    { contract: '0xdAC17F958D2ee523a2206206994597C13D831ec7', symbol: 'USDT', decimals: 6 },
    { contract: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', symbol: 'USDC', decimals: 6 },
    { contract: '0x6B175474E89094C44Da98b954EedeAC495271d0F', symbol: 'DAI', decimals: 18 },
    { contract: '0x514910771AF9Ca656af840dff83E8264EcF986CA', symbol: 'LINK', decimals: 18 },
    { contract: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', symbol: 'WETH', decimals: 18 },
    { contract: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', symbol: 'WBTC', decimals: 8 },
  ],
  polygon: [
    { contract: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', symbol: 'USDC', decimals: 6 },
    { contract: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063', symbol: 'DAI', decimals: 18 },
    { contract: '0x7d1afa7b718fb893db30a3abc0cfc608aacfebb0', symbol: 'MATIC', decimals: 18 },
    { contract: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270', symbol: 'WMATIC', decimals: 18 },
  ],
  bsc: [
    { contract: '0x55d398326f99059fF775485246999027B3197955', symbol: 'USDT', decimals: 18 },
    { contract: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d', symbol: 'USDC', decimals: 18 },
    { contract: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3', symbol: 'DAI', decimals: 18 },
    { contract: '0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c', symbol: 'BTCB', decimals: 18 },
    { contract: '0x0e09fabb73bd3ade3e7c2c1b01a1b8ddc7e7ab', symbol: 'CAKE', decimals: 18 },
  ],
};

const EVM_CHAINS = ['ethereum', 'polygon', 'bsc'];
const FET_ALIAS_CHAINS = ['fet', 'asi', 'fetchhub'];
const BACKEND_NATIVE_BALANCE_CHAINS = [...EVM_CHAINS, 'bitcoin', 'sui', 'akash', ...FET_ALIAS_CHAINS];
const WALLET_CHAIN_OPTIONS = [
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'bsc', label: 'Binance Smart Chain' },
  { value: 'akash', label: 'Akash' },
  { value: 'fet', label: 'Fetch.ai / ASI' },
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'solana', label: 'Solana' },
  { value: 'sui', label: 'Sui' },
  { value: 'tao', label: 'TAO / Bittensor' },
];

const createEmptyWalletForm = () => ({ address: '', chain: 'ethereum', label: '' });
const createEmptyCustomTokenForm = () => ({ contract: '', symbol: '', decimals: '18' });

const getWalletChainLabel = (chain) => {
  if (FET_ALIAS_CHAINS.includes(chain)) {
    return 'Fetch.ai / ASI';
  }

  return {
    ethereum: 'Ethereum',
    polygon: 'Polygon',
    bsc: 'Binance Smart Chain',
    akash: 'Akash',
    bitcoin: 'Bitcoin',
    solana: 'Solana',
    sui: 'Sui',
    tao: 'TAO / Bittensor',
  }[chain] || chain || 'On-chain';
};

const formatTokenBalance = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value || '0');
  }
  if (Math.abs(numericValue) >= 1000) {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(numericValue);
  }
  if (Math.abs(numericValue) >= 1) {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(numericValue);
  }
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 8 }).format(numericValue);
};

const formatWalletPreview = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }
  const normalized = value.trim();
  if (!normalized) {
    return '';
  }
  if (normalized.length <= 18) {
    return normalized;
  }
  return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
};

const isLikelyNetworkIssue = (message) => /failed to fetch|network|err_network|err_connection|timeout|timed out|502|503|504/i.test(String(message || ''));

const walletActionButtonSx = {
  borderRadius: 999,
  px: 1.6,
  py: 0.9,
  fontWeight: 800,
  textTransform: 'none',
  minWidth: 0,
};

const OnChainWalletsSection = ({ userId, syncAllRequestId = 0, onSyncAllComplete }) => {
  const muiTheme = useMuiTheme();
  const fullScreenDialog = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const updateOnchainAsset = usePortfolioStore(s => s.updateOnchainAsset);
  const removeOnchainWallet = usePortfolioStore(s => s.removeOnchainWallet);
  const [wallets, setWallets] = useState([]);
  const setOnchainAssets = usePortfolioStore(s => s.setOnchainAssets);
  // Always sync Zustand onchainAssets with backend wallet list
  const refreshWallets = React.useCallback(async ({ notifyOverview = false } = {}) => {
    try {
      const list = await getWalletAddresses();
      const nextWallets = Array.isArray(list) ? list : [];
      setWallets(nextWallets);

      // Keep already synchronized token/native rows and only align wallet metadata.
      const walletById = new Map(nextWallets.map((wallet) => [String(wallet.id), wallet]));
      const currentOnchainAssets = normalizeStoreOnchainAssets(usePortfolioStore.getState().onchainAssets || []);
      const nextOnchainAssets = currentOnchainAssets
        .filter((asset) => {
          const walletId = asset?.walletId ?? asset?.id;
          if (walletId == null) {
            return true;
          }
          return walletById.has(String(walletId));
        })
        .map((asset) => {
          const walletId = asset?.walletId ?? asset?.id;
          const wallet = walletId == null ? null : walletById.get(String(walletId));
          if (!wallet) {
            return asset;
          }
          const walletChain = getWalletChain(wallet);
          return {
            ...asset,
            walletId: wallet.id,
            address: wallet.address,
            label: wallet.label,
            chain: walletChain,
            blockchain: walletChain,
          };
        });
      setOnchainAssets(nextOnchainAssets);

      if (notifyOverview) {
        window.dispatchEvent(new Event('refresh-connection-entities'));
      }
      return nextWallets;
    } catch (err) {
      setWallets([]);
      if (notifyOverview) {
        window.dispatchEvent(new Event('refresh-connection-entities'));
      }
      return [];
    }
  }, [setOnchainAssets]);

  React.useEffect(() => {
    void refreshWallets();
  }, [refreshWallets]);
  const handleRemoveWallet = async (walletId) => {
    setLoading(l => ({ ...l, [walletId]: true }));
    try {
      await deleteWalletAddress(walletId);
      removeOnchainWallet(walletId);
      // Always refresh wallets from backend after removal
      await refreshWallets({ notifyOverview: true });
      window.dispatchEvent(new Event('refresh-connection-sync-state'));
    } catch (err) {
      setError(e => ({ ...e, [walletId]: err.message || 'Erreur suppression' }));
    } finally {
      setLoading(l => ({ ...l, [walletId]: false }));
    }
  };
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({}); // wallet.id -> message d'erreur général
  const [errorMsg, setErrorMsg] = useState({}); // wallet.id -> message d'erreur détaillé
  const [globalError, setGlobalError] = useState(''); // erreur globale
  const [tokenBalances, setTokenBalances] = useState({}); // wallet.id -> [{symbol, balance}]
  // ...existing code...

  // Custom token addition per wallet
  const [customTokens, setCustomTokens] = useState({}); // wallet.id -> array of {contract, symbol, decimals}
  const [customTokenWallet, setCustomTokenWallet] = useState(null);
  const [customTokenForm, setCustomTokenForm] = useState(createEmptyCustomTokenForm());
  const [customTokenError, setCustomTokenError] = useState('');

  const handleAddCustomToken = (walletId, token) => {
    setCustomTokens(ct => ({
      ...ct,
      [walletId]: [...(ct[walletId] || []), token]
    }));
  };

  // Stockage des dates de synchronisation
  const [lastSync, setLastSync] = useState({}); // wallet.id -> date

  // Met à jour la date de synchronisation après chaque sync
  const handleSync = React.useCallback(async (wallet) => {
    const walletChain = getWalletChain(wallet);
    const isEvmChain = EVM_CHAINS.includes(walletChain);
    const supportsBackendNativeBalance = BACKEND_NATIVE_BALANCE_CHAINS.includes(walletChain);
    console.log('[Sync] Clicked for wallet:', wallet);
    setLoading(l => ({ ...l, [wallet.id]: true }));
    setError(e => ({ ...e, [wallet.id]: '' }));
    setErrorMsg(em => ({ ...em, [wallet.id]: '' }));
    setGlobalError('');
    try {
      console.log('[Sync] Start sync for', walletChain, wallet.address);
      let native = 0;
      let syncedQuantity = typeof wallet.quantity === 'number' ? wallet.quantity : Number(wallet.quantity) || 0;
      let balances = [];
      // N'exécute fetchNativeBalance que pour les chaînes supportées par le backend
      if (supportsBackendNativeBalance) {
        native = await fetchNativeBalance({ address: wallet.address, chain: walletChain });
        syncedQuantity = native;
        console.log('[Sync] Native balance:', native);
        console.log('[Sync] updateOnchainAsset for', walletChain, 'with quantity:', native);
        updateOnchainAsset({
          ...wallet,
          chain: walletChain,
          blockchain: walletChain,
          assetId: getNativeAssetIdForChain(walletChain),
          quantity: native,
        });
      } else if (walletChain === 'solana') {
        const assets = await OnchainService.fetchAssets({
          id: wallet.id,
          label: wallet.label || '',
          address: wallet.address,
          chain: 'solana',
          createdAt: Date.now(),
        });
        const solAsset = assets.find(a => a.symbol === 'SOL');
        const solBalance = solAsset ? solAsset.quantity : 0;
        syncedQuantity = solBalance;
        balances = assets
          .filter(asset => asset.symbol !== 'SOL')
          .map(asset => ({ symbol: asset.symbol, balance: asset.quantity }));
        console.log('[Sync] SOL balance (frontend RPC):', solBalance);
        updateOnchainAsset({
          ...wallet,
          chain: walletChain,
          blockchain: walletChain,
          assetId: 'SOL',
          quantity: solBalance,
        });
      } else if (walletChain === 'tao') {
        // Pour TAO, fetch la vraie balance via le backend
        const assets = await OnchainService.fetchAssets({
          id: wallet.id,
          label: wallet.label || '',
          address: wallet.address,
          chain: 'tao',
          createdAt: Date.now()
        });
        const taoAsset = assets.find(a => a.symbol === 'TAO');
        const taoBalance = taoAsset ? taoAsset.quantity : 0;
        syncedQuantity = taoBalance;
        console.log('[Sync] TAO balance (backend):', taoBalance);
        if (taoAsset === undefined && assets.length === 0) {
          setErrorMsg(em => ({ ...em, [wallet.id]: "Impossible de récupérer la balance TAO (explorer hors ligne ou adresse inconnue)" }));
        }
        updateOnchainAsset({
          ...wallet,
          chain: walletChain,
          blockchain: walletChain,
          assetId: 'TAO',
          quantity: taoBalance,
        });
      } else {
        // Autres blockchains non supportées
        const fallbackQuantity = typeof wallet.quantity === 'number' ? wallet.quantity : Number(wallet.quantity) || 0;
        syncedQuantity = fallbackQuantity;
        console.log('[Sync] (Non-EVM) updateOnchainAsset for', walletChain, 'with quantity:', fallbackQuantity);
        updateOnchainAsset({
          ...wallet,
          chain: walletChain,
          blockchain: walletChain,
          assetId: getNativeAssetIdForChain(walletChain),
          quantity: fallbackQuantity,
        });
      }
      setWallets(prev => prev.map((currentWallet) => {
        if (String(currentWallet.id) !== String(wallet.id)) return currentWallet;
        return {
          ...currentWallet,
          chain: walletChain,
          blockchain: walletChain,
          quantity: syncedQuantity
        };
      }));
      // Pour TAO et autres, ignorer ou gérer séparément
      const tokens = TOKEN_LISTS[walletChain] || [];
      if (tokens.length > 0 && isEvmChain) {
        balances = await fetchTokenBalances({ address: wallet.address, chain: walletChain, tokens });
        console.log('[Sync] Token balances:', balances);
      }
      const custom = customTokens[wallet.id] || [];
      let customBalances = [];
      if (custom.length > 0 && isEvmChain) {
        customBalances = await fetchTokenBalances({ address: wallet.address, chain: walletChain, tokens: custom });
        console.log('[Sync] Custom token balances:', customBalances);
      }
      setTokenBalances(tb => ({ ...tb, [wallet.id]: [...balances, ...customBalances] }));

      if (isEvmChain) {
        const currentOnchainAssets = Array.isArray(usePortfolioStore.getState().onchainAssets)
          ? usePortfolioStore.getState().onchainAssets
          : [];
        const preservedOnchainAssets = currentOnchainAssets.filter((asset) => String(asset?.walletId) !== String(wallet.id));
        const nativeOnchainAsset = supportsBackendNativeBalance
          ? [{
            ...wallet,
            id: wallet.id,
            walletId: wallet.id,
            chain: walletChain,
            blockchain: walletChain,
            assetId: getNativeAssetIdForChain(walletChain),
            quantity: syncedQuantity,
            sourceType: 'ONCHAIN',
            type: 'ONCHAIN',
          }]
          : [];
        const tokenOnchainAssets = [...balances, ...customBalances]
          .filter((token) => typeof token?.balance === 'number' && token.balance > 0)
          .map((token) => ({
            id: `${wallet.id}:${String(token.symbol).toUpperCase()}`,
            walletId: wallet.id,
            chain: walletChain,
            blockchain: walletChain,
            assetId: String(token.symbol || '').trim().toUpperCase(),
            quantity: token.balance,
            sourceType: 'ONCHAIN',
            type: 'ONCHAIN',
          }));

        usePortfolioStore.getState().setOnchainAssets([
          ...preservedOnchainAssets,
          ...nativeOnchainAsset,
          ...tokenOnchainAssets,
        ]);
      }

      // Enregistre la date de synchronisation
      const syncTime = createSyncTimestamp();
      setLastSync(ls => ({ ...ls, [wallet.id]: syncTime }));
      localStorage.setItem('ecotoneai_lastSync_wallet_' + wallet.id, syncTime);
      window.dispatchEvent(new Event('refresh-connection-sync-state'));
      console.log('[Sync] Sync finished for', wallet.address);
    } catch (err) {
      setError(e => ({ ...e, [wallet.id]: err.message || 'Erreur API' }));
      let msg = 'Erreur réseau ou API.';
      if (walletChain === 'akash') {
        msg = 'Impossible de récupérer la balance Akash (endpoint LCD indisponible ou adresse inconnue).';
      } else if (FET_ALIAS_CHAINS.includes(walletChain)) {
        msg = 'Impossible de récupérer la balance Fetch.ai / ASI (endpoint LCD indisponible ou adresse inconnue).';
      }
      if (err && err.message) msg += ' (' + err.message + ')';
      if (isLikelyNetworkIssue(err?.message)) {
        msg += ' Si un VPN est actif, désactivez-le temporairement puis réessayez.';
      }
      setErrorMsg(em => ({ ...em, [wallet.id]: msg }));
      // Si toutes les sync échouent, affiche une erreur globale
      const allFailed = wallets.every(w => w.id === wallet.id || error[w.id]);
      if (allFailed) setGlobalError('Synchronisation échouée pour tous les wallets on-chain.');
      console.error('[Sync] Error during sync:', err);
    } finally {
      setLoading(l => ({ ...l, [wallet.id]: false }));
      console.log('[Sync] End sync for', wallet.address);
    }
  }, [customTokens, error, updateOnchainAsset, wallets]);

  const handleSyncAll = React.useCallback(async () => {
    const walletsToSync = wallets.length > 0 ? wallets : await refreshWallets();
    for (const wallet of walletsToSync) {
      await handleSync(wallet);
    }
  }, [handleSync, refreshWallets, wallets]);

  const lastHandledSyncAllRequestRef = React.useRef(0);
  React.useEffect(() => {
    if (!syncAllRequestId || syncAllRequestId === lastHandledSyncAllRequestRef.current) {
      return;
    }

    lastHandledSyncAllRequestRef.current = syncAllRequestId;
    let cancelled = false;

    const run = async () => {
      try {
        await handleSyncAll();
      } finally {
        if (!cancelled && onSyncAllComplete) {
          onSyncAllComplete('wallets', syncAllRequestId);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [handleSyncAll, onSyncAllComplete, syncAllRequestId]);

  // Charge la date de synchronisation au montage
  React.useEffect(() => {
    const syncs = {};
    wallets.forEach(w => {
      const val = localStorage.getItem('ecotoneai_lastSync_wallet_' + w.id);
      if (val) syncs[w.id] = val;
    });
    setLastSync(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(syncs)) return syncs;
      return prev;
    });
  }, [wallets]);

  const handleRemoveCustomToken = (walletId, symbol) => {
    setCustomTokens(ct => ({
      ...ct,
      [walletId]: (ct[walletId] || []).filter(t => t.symbol !== symbol)
    }));
  };

  const handleOpenCustomTokenForm = React.useCallback((wallet) => {
    setCustomTokenWallet(wallet);
    setCustomTokenForm(createEmptyCustomTokenForm());
    setCustomTokenError('');
  }, []);

  const handleCloseCustomTokenForm = React.useCallback(() => {
    setCustomTokenWallet(null);
    setCustomTokenForm(createEmptyCustomTokenForm());
    setCustomTokenError('');
  }, []);

  const handleCustomTokenFormChange = React.useCallback((field, value) => {
    setCustomTokenForm((current) => ({ ...current, [field]: value }));
  }, []);

  const handleCustomTokenFormSubmit = React.useCallback((e) => {
    e.preventDefault();

    if (!customTokenWallet) {
      return;
    }

    const normalizedContract = customTokenForm.contract.trim();
    const normalizedSymbol = customTokenForm.symbol.trim().toUpperCase();
    const normalizedDecimals = Number(customTokenForm.decimals);

    if (!normalizedContract || !normalizedSymbol || !Number.isInteger(normalizedDecimals) || normalizedDecimals <= 0) {
      setCustomTokenError('Renseignez un contrat, un symbole et un nombre de décimales valide.');
      return;
    }

    const existingTokens = customTokens[customTokenWallet.id] || [];
    const duplicateToken = existingTokens.some((token) => {
      const existingContract = String(token.contract || '').trim().toLowerCase();
      const existingSymbol = String(token.symbol || '').trim().toUpperCase();
      return existingContract === normalizedContract.toLowerCase() || existingSymbol === normalizedSymbol;
    });

    if (duplicateToken) {
      setCustomTokenError('Ce token custom existe déjà pour ce wallet.');
      return;
    }

    handleAddCustomToken(customTokenWallet.id, {
      contract: normalizedContract,
      symbol: normalizedSymbol,
      decimals: normalizedDecimals,
    });
    handleCloseCustomTokenForm();
  }, [customTokenForm, customTokenWallet, customTokens, handleCloseCustomTokenForm]);

  // --- Edition wallet ---
  const [editWallet, setEditWallet] = useState(null);
  const [showWalletForm, setShowWalletForm] = useState(false);
  const [walletForm, setWalletForm] = useState(createEmptyWalletForm());
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const walletFormPanelSx = {
    borderRadius: 4,
    border: `1px solid ${alpha('#7c6335', 0.16)}`,
    background: 'rgba(255,255,255,0.72)',
    boxShadow: '0 18px 40px rgba(25, 36, 46, 0.08)',
    backdropFilter: 'blur(16px)',
    p: { xs: 2, sm: 2.5 },
  };

  React.useEffect(() => {
    if (editWallet) {
      setWalletForm({
        address: editWallet.address || '',
        chain: editWallet.chain || 'ethereum',
        label: editWallet.label || ''
      });
    } else {
      setWalletForm(createEmptyWalletForm());
    }
  }, [editWallet, showWalletForm]);

  const handleCloseWalletForm = React.useCallback(() => {
    if (formLoading) {
      return;
    }
    setShowWalletForm(false);
    setEditWallet(null);
    setFormError('');
  }, [formLoading]);

  const handleOpenCreateWallet = React.useCallback(() => {
    setFormError('');
    setEditWallet(null);
    setWalletForm(createEmptyWalletForm());
    setShowWalletForm(true);
  }, []);

  const handleEditWallet = (wallet) => {
    setFormError('');
    setEditWallet(wallet);
    setShowWalletForm(true);
  };

  const handleWalletFormChange = (field, value) => {
    setWalletForm(f => ({ ...f, [field]: value }));
  };

  const handleWalletFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    const normalizedWalletForm = {
      address: walletForm.address.trim(),
      chain: walletForm.chain,
      label: walletForm.label.trim(),
    };
    try {
      if (editWallet) {
        await updateWalletAddress(editWallet.id, normalizedWalletForm);
        await refreshWallets({ notifyOverview: true });
        setEditWallet(null);
      } else {
        if (!userId) {
          throw new Error('Aucun utilisateur connecté pour enregistrer ce wallet.');
        }
        const entity = await createConnectionEntity({
          userId,
          type: 'WALLET',
          name: normalizedWalletForm.chain,
          status: 'ACTIVE'
        });
        await createWalletAddress({
          connectionEntity: { id: entity.id },
          address: normalizedWalletForm.address,
          chain: normalizedWalletForm.chain,
          label: normalizedWalletForm.label
        });
        await refreshWallets({ notifyOverview: true });
        window.dispatchEvent(new Event('refresh-connection-sync-state'));
      }
      setShowWalletForm(false);
      setWalletForm(createEmptyWalletForm());
    } catch (err) {
      setFormError(err.message || 'Erreur inconnue');
    } finally {
      setFormLoading(false);
    }
  };

  const walletModeLabel = editWallet ? 'Mode : édition' : 'Mode : création';
  const selectedChainOption = WALLET_CHAIN_OPTIONS.find((option) => option.value === walletForm.chain);
  const walletModalPreview = formatWalletPreview(editWallet?.address || walletForm.address);
  const customTokenWalletChain = customTokenWallet ? getWalletChain(customTokenWallet) : '';
  const customTokenWalletPreview = formatWalletPreview(customTokenWallet?.address || '');

  return (
    <div className="onchain-wallets-section">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
        <div className="section-title">Wallets on-chain connectés</div>
        <Button
          type="button"
          onClick={handleOpenCreateWallet}
          startIcon={<AddRoundedIcon />}
          variant="contained"
          sx={{
            borderRadius: 999,
            px: 2.1,
            py: 1,
            fontWeight: 800,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #17323c 0%, #2d5565 100%)',
            boxShadow: '0 16px 30px rgba(23, 50, 60, 0.18)',
            '&:hover': {
              background: 'linear-gradient(135deg, #12262d 0%, #254755 100%)',
            },
          }}
        >
          Ajouter un wallet
        </Button>
      </Stack>

      <Dialog
        open={showWalletForm}
        onClose={handleCloseWalletForm}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreenDialog}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(15, 23, 32, 0.58)',
              backdropFilter: 'blur(8px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            overflow: 'hidden',
            borderRadius: { xs: 0, sm: 4 },
            border: '1px solid rgba(123, 97, 48, 0.16)',
            background: 'linear-gradient(180deg, #fdf8ef 0%, #f5eee1 100%)',
            boxShadow: '0 32px 80px rgba(16, 26, 35, 0.28)',
          },
        }}
      >
        <Box
          component="form"
          onSubmit={handleWalletFormSubmit}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at top left, rgba(214, 170, 83, 0.20), transparent 35%), radial-gradient(circle at top right, rgba(41, 121, 255, 0.10), transparent 26%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', px: { xs: 2.25, sm: 3 }, pt: { xs: 2.25, sm: 3 }, pb: 2.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: '0.18em',
                    fontWeight: 700,
                    color: '#8b6c2d',
                  }}
                >
                  Wallet on-chain
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c', lineHeight: 1.1, mb: 0.75 }}>
                  {editWallet ? 'Gérer le wallet' : 'Ajouter un wallet'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.78)', maxWidth: 460 }}>
                  {editWallet
                    ? 'Modifiez le label, l’adresse ou la blockchain sans quitter la vue des connexions.'
                    : 'Ajoutez un wallet suivi dans l’interface pour centraliser sa valorisation et sa synchronisation.'}
                </Typography>
              </Box>

              <IconButton
                onClick={handleCloseWalletForm}
                aria-label="Fermer l’éditeur wallet"
                disabled={formLoading}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.72)',
                  border: '1px solid rgba(23, 50, 60, 0.08)',
                  boxShadow: '0 10px 24px rgba(15, 23, 32, 0.08)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.94)',
                  },
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
              <Chip
                icon={<AccountBalanceWalletRoundedIcon />}
                label={walletModeLabel}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(123, 97, 48, 0.14)',
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`Réseau : ${selectedChainOption?.label || walletForm.chain}`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(123, 97, 48, 0.14)',
                  fontWeight: 700,
                }}
              />
              {walletModalPreview ? (
                <Chip
                  label={walletModalPreview}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(123, 97, 48, 0.14)',
                    fontWeight: 700,
                  }}
                />
              ) : null}
            </Stack>
          </Box>

          <Stack spacing={2} sx={{ px: { xs: 2.25, sm: 3 }, pb: { xs: 2.25, sm: 3 } }}>
            <Box sx={walletFormPanelSx}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 0.5 }}>
                    Identité du wallet
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.72)' }}>
                    Le label est utilisé dans les vues portfolio et les blocs de synthèse pour identifier rapidement la source on-chain.
                  </Typography>
                </Box>

                <TextField
                  label="Label"
                  value={walletForm.label}
                  onChange={e => handleWalletFormChange('label', e.target.value)}
                  placeholder="Ex: Wallet Binance"
                  fullWidth
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(255,255,255,0.88)',
                    },
                  }}
                />
              </Stack>
            </Box>

            <Box sx={walletFormPanelSx}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 0.5 }}>
                    Adresse et réseau
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.72)' }}>
                    L’adresse est utilisée pour la récupération du solde natif et des tokens associés au wallet.
                  </Typography>
                </Box>

                <TextField
                  label="Adresse du wallet"
                  value={walletForm.address}
                  onChange={e => handleWalletFormChange('address', e.target.value)}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(255,255,255,0.88)',
                    },
                  }}
                />

                <FormControl fullWidth>
                  <InputLabel id="wallet-chain-label">Blockchain</InputLabel>
                  <Select
                    labelId="wallet-chain-label"
                    value={walletForm.chain}
                    label="Blockchain"
                    onChange={e => handleWalletFormChange('chain', e.target.value)}
                    sx={{
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(255,255,255,0.88)',
                    }}
                  >
                    {WALLET_CHAIN_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            {formError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {formError}
              </Alert>
            ) : null}

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1.5}
              sx={{ px: 0.25, pt: 0.25 }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(23, 50, 60, 0.65)' }}>
                Les changements sont enregistrés immédiatement puis reflétés dans les vues connexions et portfolio.
              </Typography>

              <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1}>
                <Button
                  type="button"
                  onClick={handleCloseWalletForm}
                  disabled={formLoading}
                  sx={{
                    borderRadius: 999,
                    px: 2.25,
                    py: 1.1,
                    fontWeight: 800,
                    textTransform: 'none',
                    color: '#17323c',
                  }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={formLoading || !walletForm.address.trim()}
                  startIcon={formLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : (editWallet ? <EditRoundedIcon /> : <AddRoundedIcon />)}
                  sx={{
                    borderRadius: 999,
                    px: 2.25,
                    py: 1.1,
                    fontWeight: 800,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #17323c 0%, #2d5565 100%)',
                    boxShadow: '0 16px 30px rgba(23, 50, 60, 0.18)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #12262d 0%, #254755 100%)',
                    },
                  }}
                >
                  {formLoading ? (editWallet ? 'Enregistrement...' : 'Création...') : (editWallet ? 'Enregistrer' : 'Ajouter')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(customTokenWallet)}
        onClose={handleCloseCustomTokenForm}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreenDialog}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(15, 23, 32, 0.58)',
              backdropFilter: 'blur(8px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            overflow: 'hidden',
            borderRadius: { xs: 0, sm: 4 },
            border: '1px solid rgba(123, 97, 48, 0.16)',
            background: 'linear-gradient(180deg, #fdf8ef 0%, #f5eee1 100%)',
            boxShadow: '0 32px 80px rgba(16, 26, 35, 0.28)',
          },
        }}
      >
        <Box
          component="form"
          onSubmit={handleCustomTokenFormSubmit}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at top left, rgba(214, 170, 83, 0.20), transparent 35%), radial-gradient(circle at top right, rgba(41, 121, 255, 0.10), transparent 26%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', px: { xs: 2.25, sm: 3 }, pt: { xs: 2.25, sm: 3 }, pb: 2.25 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: '0.18em',
                    fontWeight: 700,
                    color: '#8b6c2d',
                  }}
                >
                  Token custom
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c', lineHeight: 1.1, mb: 0.75 }}>
                  Ajouter un token au wallet
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.78)', maxWidth: 460 }}>
                  Ajoutez un contrat personnalisé pour l’inclure dans les balances synchronisées de ce wallet EVM.
                </Typography>
              </Box>

              <IconButton
                onClick={handleCloseCustomTokenForm}
                aria-label="Fermer l’éditeur de token"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.72)',
                  border: '1px solid rgba(23, 50, 60, 0.08)',
                  boxShadow: '0 10px 24px rgba(15, 23, 32, 0.08)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.94)',
                  },
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
              <Chip
                icon={<AccountBalanceWalletRoundedIcon />}
                label={customTokenWallet?.label || 'Wallet sans label'}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(123, 97, 48, 0.14)',
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`Réseau : ${customTokenWalletChain || 'EVM'}`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(123, 97, 48, 0.14)',
                  fontWeight: 700,
                }}
              />
              {customTokenWalletPreview ? (
                <Chip
                  label={customTokenWalletPreview}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(123, 97, 48, 0.14)',
                    fontWeight: 700,
                  }}
                />
              ) : null}
            </Stack>
          </Box>

          <Stack spacing={2} sx={{ px: { xs: 2.25, sm: 3 }, pb: { xs: 2.25, sm: 3 } }}>
            <Box sx={walletFormPanelSx}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#17323c', mb: 0.5 }}>
                    Contrat et symbole
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(23, 50, 60, 0.72)' }}>
                    Renseignez l’adresse du contrat et le symbole à suivre lors des prochaines synchronisations du wallet.
                  </Typography>
                </Box>

                <TextField
                  label="Adresse du contrat"
                  value={customTokenForm.contract}
                  onChange={e => handleCustomTokenFormChange('contract', e.target.value)}
                  placeholder="0x..."
                  required
                  fullWidth
                  autoFocus
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(255,255,255,0.88)',
                    },
                  }}
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <TextField
                    label="Symbole"
                    value={customTokenForm.symbol}
                    onChange={e => handleCustomTokenFormChange('symbol', e.target.value.toUpperCase())}
                    placeholder="USDT"
                    required
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5,
                        backgroundColor: 'rgba(255,255,255,0.88)',
                      },
                    }}
                  />

                  <TextField
                    label="Décimales"
                    value={customTokenForm.decimals}
                    onChange={e => handleCustomTokenFormChange('decimals', e.target.value)}
                    type="number"
                    required
                    inputProps={{ min: 1, step: 1 }}
                    sx={{
                      minWidth: { xs: '100%', sm: 140 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5,
                        backgroundColor: 'rgba(255,255,255,0.88)',
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Box>

            {customTokenError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {customTokenError}
              </Alert>
            ) : null}

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1.5}
              sx={{ px: 0.25, pt: 0.25 }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(23, 50, 60, 0.65)' }}>
                Le token est ajouté localement au wallet courant et sera interrogé lors des prochaines synchronisations EVM.
              </Typography>

              <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1}>
                <Button
                  type="button"
                  onClick={handleCloseCustomTokenForm}
                  sx={{
                    borderRadius: 999,
                    px: 2.25,
                    py: 1.1,
                    fontWeight: 800,
                    textTransform: 'none',
                    color: '#17323c',
                  }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!customTokenForm.contract.trim() || !customTokenForm.symbol.trim() || !String(customTokenForm.decimals).trim()}
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    borderRadius: 999,
                    px: 2.25,
                    py: 1.1,
                    fontWeight: 800,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #17323c 0%, #2d5565 100%)',
                    boxShadow: '0 16px 30px rgba(23, 50, 60, 0.18)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #12262d 0%, #254755 100%)',
                    },
                  }}
                >
                  Ajouter le token
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Dialog>

      <div style={{ color: '#1976d2', fontWeight: 500, marginBottom: 8 }}>
        {wallets.length} connexion{wallets.length > 1 ? 's' : ''} affichée{wallets.length > 1 ? 's' : ''}
      </div>
      {wallets.length === 0 && <div>Aucun wallet on-chain</div>}
      {wallets.map((w, idx) => {
        const walletChain = getWalletChain(w);
        const walletChainLabel = getWalletChainLabel(walletChain);
        const nativeAssetId = getNativeAssetIdForChain(walletChain);
        const walletTokenBalances = tokenBalances[w.id] || [];
        const walletCustomTokens = customTokens[w.id] || [];
        return (
        <div className="wallet-card" key={w.id}>
          <div className="wallet-blockchain">{walletChainLabel}</div>
          <div className="wallet-summary">
            <div className="wallet-label" style={{ fontWeight: 600, marginBottom: 2 }}>{w.label || <span style={{ color: '#888' }}>Sans label</span>}</div>
            <div className="wallet-address">{w.address ? w.address.slice(0, 6) + '...' + w.address.slice(-3) : w.address}</div>
          </div>
          <div className="wallet-meta">
            <div className="wallet-assets">
              {w.quantity || 0} {nativeAssetId !== 'ONCHAIN' ? nativeAssetId : ''}
              {walletTokenBalances.length > 0 ? (
                <Box
                  sx={{
                    mt: 1.2,
                    p: 1.1,
                    borderRadius: 2.5,
                    background: 'rgba(255,255,255,0.72)',
                    border: '1px solid rgba(23, 50, 60, 0.08)',
                  }}
                >
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.9, color: '#6b7b82', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Tokens détectés
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {walletTokenBalances.map((t) => (
                      <Chip
                        key={`${w.id}-${t.symbol}-${t.balance}`}
                        size="small"
                        label={`${t.symbol} · ${formatTokenBalance(t.balance)}`}
                        sx={{
                          bgcolor: 'rgba(23, 50, 60, 0.08)',
                          color: '#17323c',
                          border: '1px solid rgba(23, 50, 60, 0.10)',
                          fontWeight: 700,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : null}
              {walletCustomTokens.length > 0 ? (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.1,
                    borderRadius: 2.5,
                    background: 'rgba(29, 78, 216, 0.05)',
                    border: '1px solid rgba(29, 78, 216, 0.10)',
                  }}
                >
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.9, color: '#5177a1', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Tokens custom suivis
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {walletCustomTokens.map(t => (
                    <Chip
                      key={`${w.id}-${t.contract}-${t.symbol}`}
                      size="small"
                      label={`${t.symbol} · ${t.decimals}d`}
                      onDelete={() => handleRemoveCustomToken(w.id, t.symbol)}
                      sx={{
                        bgcolor: 'rgba(29, 78, 216, 0.10)',
                        color: '#1d4ed8',
                        border: '1px solid rgba(29, 78, 216, 0.14)',
                        fontWeight: 700,
                      }}
                    />
                  ))}
                  </Stack>
                </Box>
              ) : null}
            </div>
            {EVM_CHAINS.includes(walletChain) && (
              <div className="wallet-token-form">
                <Button
                  type="button"
                  onClick={() => handleOpenCustomTokenForm(w)}
                  variant="outlined"
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    borderRadius: 999,
                    px: 1.75,
                    py: 0.85,
                    fontWeight: 800,
                    textTransform: 'none',
                    color: '#17323c',
                    borderColor: 'rgba(23, 50, 60, 0.18)',
                    backgroundColor: 'rgba(255,255,255,0.74)',
                    '&:hover': {
                      borderColor: 'rgba(23, 50, 60, 0.3)',
                      backgroundColor: 'rgba(255,255,255,0.94)',
                    },
                  }}
                >
                  Ajouter un token custom
                </Button>
              </div>
            )}
            <div className="wallet-sync">
              <div className="wallet-sync-time" style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
                Dernière synchronisation : {formatSyncDate(lastSync[w.id])}
              </div>
              {errorMsg[w.id] && (
                <div className="error" style={{ color: '#c62828', fontSize: 13, marginTop: 4 }}>{errorMsg[w.id]}</div>
              )}
              {error[w.id] && <div className="error" style={{ marginTop: 4 }}>{error[w.id]}</div>}
            </div>
          </div>
          <div className="wallet-actions">
            <Button
              type="button"
              onClick={() => { console.log('[Sync] Button pressed for', w); handleSync(w); }}
              disabled={loading[w.id]}
              title="Actualise le solde et les tokens du wallet."
              variant="contained"
              startIcon={loading[w.id] ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AutorenewRoundedIcon />}
              sx={{
                ...walletActionButtonSx,
                background: 'linear-gradient(135deg, #17323c 0%, #2d5565 100%)',
                boxShadow: '0 14px 28px rgba(23, 50, 60, 0.16)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #12262d 0%, #254755 100%)',
                },
              }}
            >
              {loading[w.id] ? 'Synchronisation...' : 'Actualiser'}
            </Button>
            <Button
              type="button"
              onClick={() => handleRemoveWallet(w.id)}
              disabled={loading[w.id]}
              title="Supprime ce wallet on-chain"
              variant="outlined"
              startIcon={<DeleteOutlineRoundedIcon />}
              sx={{
                ...walletActionButtonSx,
                color: '#aa3d33',
                borderColor: 'rgba(170, 61, 51, 0.22)',
                backgroundColor: 'rgba(170, 61, 51, 0.06)',
                '&:hover': {
                  borderColor: 'rgba(170, 61, 51, 0.34)',
                  backgroundColor: 'rgba(170, 61, 51, 0.10)',
                },
              }}
            >
              Supprimer
            </Button>
            <Button
              type="button"
              onClick={() => handleEditWallet(w)}
              disabled={loading[w.id]}
              title="Gérer ce wallet"
              variant="outlined"
              startIcon={<EditRoundedIcon />}
              sx={{
                ...walletActionButtonSx,
                color: '#17323c',
                borderColor: 'rgba(23, 50, 60, 0.18)',
                backgroundColor: 'rgba(255,255,255,0.74)',
                '&:hover': {
                  borderColor: 'rgba(23, 50, 60, 0.3)',
                  backgroundColor: 'rgba(255,255,255,0.94)',
                },
              }}
            >
              Gérer
            </Button>
          </div>
        </div>
      )})}
      {globalError && (
        <div className="global-error" style={{ color: '#c62828', fontWeight: 600, marginBottom: 12 }}>{globalError}</div>
      )}
    </div>
  );
}

export default OnChainWalletsSection;
